<script>

  import { gsap } from 'gsap'
  import { Draggable } from 'gsap/Draggable'
  import { InertiaPlugin } from 'gsap/InertiaPlugin'
  import debounce from 'lodash.debounce'

  export default {
    props: {
      activeSlide: {
        type: Number,
        default: 0
      },
      spaceBetween: {
        type: Number,
        default: 32
      },
      items: {
        type: Array,
        default: () => ([])
      },
      showProgress: {
        type: Boolean,
        default: false
      },
      smallSlider: {
        type: Boolean,
        default: false
      },
      disableDrag: {
        type: Boolean,
        default: false
      },
      centerAllSlides: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        draggable: null,
        slideWidth: 0,
        slidesCount: 0,
        totalSlidesWidth: 0,
        innerValue: 0,
        isDragging: false,
        debouncedHandler: null
      }
    },
    computed: {
      active: {
        get () {
          return this.activeSlide || this.innerValue
        },
        set (val) {
          this.innerValue = val
        }
      },
      hasNext () {
        if (this.slideWidth > 0) {
          return -this.active * (this.slideWidth + this.spaceBetween) > this.dragBounds().minX
        } else {
          return true
        }
      },
      maxActive () {
        const slideWithSpacing = this.slideWidth + this.spaceBetween
        return Math.max(0, this.slidesCount - 1)
      }
    },
    watch: {
      items (val) {
        this.$nextTick(() => {
          this.setup()
        })
      },
      activeSlide (newVal, oldVal) {
        this.active = newVal
        if (!this.isDragging) {
          this.animateToSlide()
        }
      },
      hasNext (val) {
        this.$emit('has-next', val)
      },
      disableDrag (newVal) {
        if (this.draggable) {
          this.draggable.kill()
          this.draggable = null
        }
        this.createDraggable()
      }
    },
    mounted () {
      gsap.registerPlugin(Draggable, InertiaPlugin)
      this.setup()
    },
    beforeDestroy () {
      if (this.draggable) {
        this.draggable.kill()
      }
      window.removeEventListener('resize', this.debouncedHandler)
    },
    methods: {
      setup () {
        const slides = this.$el.querySelectorAll('.slide')
        if (slides.length === 0) {
          return
        }
        slides.forEach((slide, index) => {
          if (this.spaceBetween > 0) {
            slide.style.marginRight = this.spaceBetween + 'px'
            // Add extra margin to the last slide for proper spacing
            if (index === slides.length - 1) {
              slide.style.marginRight = (this.spaceBetween * 4) + 'px'
            }
          } else {
            slide.style.marginRight = '0px'
          }
        })
        this.slideWidth = slides[0].clientWidth
        this.slidesCount = slides.length
        
        if (this.spaceBetween > 0) {
          this.totalSlidesWidth = (this.slidesCount * this.slideWidth) + (this.slidesCount * this.spaceBetween) + (this.spaceBetween * 3)
        } else {
          this.totalSlidesWidth = this.slidesCount * this.slideWidth
        }
        
        this.$refs.carousel.style.width = this.totalSlidesWidth + 'px'
        this.createDraggable()
        this.debouncedHandler = debounce(this.handleResize, 500)
        window.addEventListener('resize', this.debouncedHandler)
      },

      handleResize () {
        this.draggable.applyBounds(this.dragBounds())
      },
      // internal use only
      prevSlide () {
        if (this.active === 0 || this.isDragging) return
        this.innerValue = this.innerValue - 1
        this.$emit('slide-change', this.innerValue)
        this.animateToSlide()
      },
      // internal use only
      nextSlide () {
        if (!this.hasNext || this.isDragging) return
        this.innerValue = this.innerValue + 1
        this.$emit('slide-change', this.innerValue)
        this.animateToSlide()
      },
      animateToSlide () {
        const slideWithSpacing = this.slideWidth + this.spaceBetween
        const viewportCenter = this.$el.clientWidth / 2
        const slideCenter = this.slideWidth / 2

        let distance

        if (this.centerAllSlides) {
          // Center all slides for image carousels
          distance = -(this.active * slideWithSpacing) - slideCenter + viewportCenter
        } else if (this.active === 0) {
          // First slide: align to left
          distance = 0
        } else if (this.active === this.slidesCount - 1) {
          // Last slide: align to right edge
          distance = this.dragBounds().minX
        } else {
          // Middle slides: center them
          distance = -(this.active * slideWithSpacing) - slideCenter + viewportCenter
        }

        gsap.to(this.$refs.carousel, {
          x: distance,
          duration: 0.4,
          ease: 'back.out(1.1)'
        })
      },
      dragBounds () {
        let minX = -(this.totalSlidesWidth + (this.slidesCount - 1) - this.$el.clientWidth )
        minX = this.totalSlidesWidth < this.$el.clientWidth ? 0 : minX
        return {
          minX,
          maxX: 0
        }
      },
      handleDragSnap (endValue) {
        const slideWithSpacing = this.slideWidth + this.spaceBetween
        const viewportCenter = this.$el.clientWidth / 2
        const slideCenter = this.slideWidth / 2

        let closestSlideIndex = 0
        let minDistance = Infinity

        for (let i = 0; i < this.slidesCount; i++) {
          let targetPosition

          if (this.centerAllSlides) {
            // Center all slides for image carousels
            targetPosition = -(i * slideWithSpacing) - slideCenter + viewportCenter
          } else if (i === 0) {
            // First slide: align to left
            targetPosition = 0
          } else if (i === this.slidesCount - 1) {
            // Last slide: align to right edge
            targetPosition = this.dragBounds().minX
          } else {
            // Middle slides: center them
            targetPosition = -(i * slideWithSpacing) - slideCenter + viewportCenter
          }

          const distance = Math.abs(endValue - targetPosition)

          if (distance < minDistance) {
            minDistance = distance
            closestSlideIndex = i
          }
        }

        this.innerValue = closestSlideIndex
        this.$emit('slide-change', this.innerValue)

        // Calculate final position based on slide type
        let finalPosition
        if (this.centerAllSlides) {
          finalPosition = -(closestSlideIndex * slideWithSpacing) - slideCenter + viewportCenter
        } else if (closestSlideIndex === 0) {
          finalPosition = 0
        } else if (closestSlideIndex === this.slidesCount - 1) {
          finalPosition = this.dragBounds().minX
        } else {
          finalPosition = -(closestSlideIndex * slideWithSpacing) - slideCenter + viewportCenter
        }

        return finalPosition
      },
      createDraggable () {
        if (this.disableDrag) return

        this.draggable = Draggable.create(this.$refs.carousel, {
          type: 'x',
          bounds: this.dragBounds(),
          inertia: true,
          edgeResistance: 0.95,
          snap: {
            x: endValue => this.handleDragSnap(endValue)
          },
          onDragStart: () => { this.isDragging = true },
          onClick: () => { this.isDragging = false },
          onThrowComplete: () => { this.isDragging = false }
        })[0]
      }
    }
  }
</script>

<template>
  <div class="px-4 carousel-base">
    <div
      ref="carousel"
      class="carousel-slides-wrapper"
    >
      <slot />
    </div>
  </div>
</template>

<style lang="scss">

  .carousel-base {
    @apply relative overflow-hidden z-10;

    .carousel-slides-wrapper {
      @apply inline-block whitespace-nowrap;
      font-size: 0;

      > .slide {
        @apply inline-block whitespace-normal;
      }

    }

  }

</style>
